import { Animation, Globe } from "@hscmap/stellar-globe";
import { Store, store } from "@/store";
import { dom } from "@/utils/dom";

export abstract class KeyPress {
  protected key!: string
  protected shift = false
  protected ctrl = false
  protected meta = false
  protected alt = false
  protected triggeredKeycode!: number

  start(e: KeyboardEvent) {
    if (e.key === this.key &&
      e.shiftKey === this.shift &&
      e.ctrlKey === this.ctrl &&
      e.metaKey === this.meta &&
      e.altKey === this.alt
    ) {
      this.triggeredKeycode = e.keyCode
      return true
    }
  }

  end(e: KeyboardEvent) {
    if (e.keyCode === this.triggeredKeycode) {
      return true
    }
  }

  private isDown = false
  keydown(e: KeyboardEvent) {
    if (e.target === document.body) {
      if (!this.isDown && this.start(e)) {
        e.preventDefault()
        this.isDown = true
        this.down(e)
      }
    }
  }
  keyup(e: KeyboardEvent) {
    if (this.isDown && this.end(e)) {
      e.preventDefault()
      this.isDown = false
      this.up()
    }
  }
  windowBlur() {
    if (this.isDown) {
      this.isDown = false
      this.up()
    }
  }
  protected abstract down(e: KeyboardEvent): void
  protected abstract up(): void
}

export abstract class AnimationKeyPress extends KeyPress {
  private animation?: Animation
  down() {
    const globe = store.currentGlobe()
    this.animation = globe.animation(({ dt }) => {
      this.tick(globe, dt)
    }, { cameraMotion: true })
  }
  up() {
    this.animation!.stop()
  }
  abstract tick(globe: Globe, dt: number): void
}

const keymap: KeyPress[] = []

function set() {
  const off: Array<() => void> = []
  off.push(
    dom.on(document, 'keydown', (e) => {
      for (const kp of keymap) {
        kp.keydown(e)
      }
    }),
    dom.on(document, 'keyup', (e) => {
      for (const kp of keymap) {
        kp.keyup(e)
      }
    }),
    dom.on(window, 'blur', (e) => {
      for (const kp of keymap) {
        kp.windowBlur()
      }
    }),
  )
  registerViewControllKeyPress()
  registerModules()
  return () => {
    while (off.length > 0) {
      off.pop()!()
    }
  }
}

function registerModules() {
  for (const modname of Object.keys(store.modules)) {
    const mod = store.modules[modname as keyof Store["modules"]]
    keymap.push(...mod.keypress)
  }
}

function registerViewControllKeyPress() {
  keymap.push(
    new ZoomKeyPress('ArrowUp', true, -1.e-3),
    new ZoomKeyPress('ArrowDown', true, +1.e-3),
    new PhiKeyPress('ArrowLeft', +1.e-3),
    new PhiKeyPress('ArrowRight', -1.e-3),
    new ThetaKeyPress('ArrowUp', +1.e-3),
    new ThetaKeyPress('ArrowDown', -1.e-3),
  )
}

export const keyboard = {
  set,
}

class ZoomKeyPress extends AnimationKeyPress {
  constructor(readonly key: string, readonly shift: boolean, readonly k: number) {
    super()
  }

  tick(globe: Globe, dt: number) {
    globe.viewFactory.fovy *= Math.exp(this.k * dt)
  }
}

class ThetaKeyPress extends AnimationKeyPress {
  constructor(readonly key: string, readonly k: number) { super() }
  tick(globe: Globe, dt: number) {
    const vf = globe.viewFactory
    vf.theta += this.k * vf.fovy * dt
  }
}

class PhiKeyPress extends AnimationKeyPress {
  constructor(readonly key: string, readonly k: number) { super() }
  tick(globe: Globe, dt: number) {
    const vf = globe.viewFactory
    vf.phi += this.k * vf.fovy / Math.cos(vf.theta) * dt
  }
}
